/**
 * FilterPopoverShell.jsx
 *
 * Shared chrome for the Excel-style column filter popovers
 * (ColumnFilterPopover / DateFilterPopover / NumericFilterPopover):
 *
 *   - hidden inline anchor + portal to document.body
 *   - fixed positioning by walking up to the anchoring <th> so the popover
 *     can't be clipped by the table's overflow-x-auto wrapper
 *   - repositions on scroll/resize instead of closing — scrolls that
 *     originate INSIDE the popover (e.g. its own value list) are ignored
 *   - close on outside click / Escape
 *   - keyboard: focus moves INTO the popover when it opens (it is portaled to
 *     the end of <body>, so Tab alone could never reach it) and back to the
 *     funnel when it closes from inside
 *   - follows its header if the header changes height (labels wrap, and
 *     picking a sort adds an arrow that can push one onto a second line)
 *   - sort asc/desc header row (labels supplied by the caller)
 *   - Clear / Apply footer (handlers supplied by the caller)
 *
 * The filter-specific content between header and footer is `children`.
 */
import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';

export function FilterPopoverShell({ width = 320, sortAscLabel, sortDescLabel, sortDir, onSort, onClose, onClear, onApply, children }) {
  const popRef = useRef(null);
  const anchorRef = useRef(null);
  // anchor lives in the table (parent of the popover-mounting node) — we read its
  // rect to position the popover via fixed coordinates so it can't be clipped by
  // the table's overflow-x-auto wrapper.
  const [coords, setCoords] = useState({ top: 0, left: 0, ready: false });

  // Resolve the anchoring <th> once on mount, then reposition on every scroll
  // or window resize so the popover follows its header. Previous behaviour was
  // "close on any scroll", which made the popover unusable — scrolling its own
  // value list (a typical interaction) closed it.
  const anchorThRef = useRef(null);
  const reposition = useCallback(() => {
    let el = anchorThRef.current;
    if (!el) {
      el = anchorRef.current;
      while (el && el.tagName !== 'TH') el = el.parentElement;
      anchorThRef.current = el;
    }
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const popWidth = width;
    const vw = window.innerWidth;
    let left = rect.right - popWidth;
    if (left < 8) left = 8;
    if (left + popWidth > vw - 8) left = vw - popWidth - 8;
    setCoords({ top: rect.bottom + 4, left, ready: true });
  }, [width]);

  useLayoutEffect(() => {
    reposition();
  }, [reposition]);

  // Remember who opened it (the funnel) so focus can go back there.
  const openerRef = useRef(null);
  useEffect(() => {
    openerRef.current = document.activeElement;
    return () => {
      // Only hand focus back if it was lost WITH the popover (Escape, Apply).
      // If the user closed it by clicking something else, that thing keeps it.
      const opener = openerRef.current;
      const lost = !document.activeElement || document.activeElement === document.body;
      if (lost && opener && opener.focus && document.contains(opener)) opener.focus({ preventScroll: true });
    };
  }, []);

  // Move focus in once it is VISIBLE: it renders hidden until it has measured
  // its position, and the browser silently ignores focus() on a hidden element.
  const focusedRef = useRef(false);
  useEffect(() => {
    if (!coords.ready || focusedRef.current) return;
    focusedRef.current = true;
    const pop = popRef.current;
    const first = pop && (pop.querySelector('input') || pop.querySelector('button'));
    if (first) first.focus({ preventScroll: true });
  }, [coords.ready]);

  useEffect(() => {
    const th = anchorThRef.current;
    if (!th || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => reposition());
    ro.observe(th);
    return () => ro.disconnect();
  }, [reposition]);

  useEffect(() => {
    const onDoc = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) onClose?.();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    // Re-anchor on scroll instead of closing — but ignore scrolls that originate
    // INSIDE the popover (its own value list scrolls vertically).
    const onScroll = (e) => {
      if (popRef.current && popRef.current.contains(e.target)) return;
      reposition();
    };
    const onResize = () => reposition();
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [onClose, reposition]);

  // Literal class strings (not template-built from the number) so Tailwind's
  // JIT scanner still sees them in source.
  const widthClass = width === 280 ? 'w-[280px]' : 'w-[320px]';

  const popoverNode = (
    <div
      ref={popRef}
      className={`go-drop-in fixed z-[1000] bg-white border border-black/10 rounded-lg shadow-2xl ${widthClass} text-text-dark`}
      style={{ top: coords.top, left: coords.left, visibility: coords.ready ? 'visible' : 'hidden' }}
    >
      {/* Sort row */}
      <div className="flex border-b border-black/5">
        <button
          type="button"
          onClick={() => onSort?.('asc')}
          className={clsx(
            'flex-1 px-3 py-2 text-[12px] font-medium hover:bg-surface-2 transition-colors flex items-center gap-1.5',
            sortDir === 'asc' && 'bg-gat-green/8 text-gat-greenDark font-bold'
          )}
        >
          <span className="text-gat-green">↓</span> {sortAscLabel}
        </button>
        <button
          type="button"
          onClick={() => onSort?.('desc')}
          className={clsx(
            'flex-1 px-3 py-2 text-[12px] font-medium hover:bg-surface-2 transition-colors flex items-center gap-1.5 border-l border-black/5',
            sortDir === 'desc' && 'bg-gat-green/8 text-gat-greenDark font-bold'
          )}
        >
          <span className="text-gat-green">↑</span> {sortDescLabel}
        </button>
      </div>

      {children}

      {/* Footer */}
      <div className="flex border-t border-black/5 bg-surface-2 px-2 py-2 gap-1.5 justify-end">
        <button type="button" onClick={onClear} className="px-3 py-1 text-[11.5px] font-semibold text-text-body hover:bg-surface rounded">
          Clear
        </button>
        <button
          type="button"
          onClick={onApply}
          className="px-3 py-1 text-[11.5px] font-semibold bg-gat-green text-white rounded hover:bg-gat-greenLight"
        >
          Apply
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Inline anchor so we can walk up to the <th> for positioning. */}
      <span ref={anchorRef} style={{ display: 'none' }} />
      {createPortal(popoverNode, document.body)}
    </>
  );
}
